import { z } from "zod/v4";

export const tournamentStatusSchema = z.enum(["upcoming", "ongoing", "completed", "cancelled"]);

export const tournamentGenderSchema = z.enum(["male", "female", "mixed"]);

export const tournamentEventTypeSchema = z.enum([
	"tournament",
	"fun_tournament",
	"camp",
	"clinic",
	"open_play",
	"league_day",
]);

export const tournamentLevelSchema = z.enum([
	"a_plus",
	"a",
	"b_plus",
	"b",
	"c_plus",
	"c",
	"d",
	"open",
]);

export const tournamentMatchStatusSchema = z.enum(["scheduled", "in_progress", "completed"]);

export const tournamentListItemSchema = z.object({
	category: z.string().nullable().optional(),
	end_date: z.string().nullable().optional(),
	event_type: tournamentEventTypeSchema,
	federation: z.string(),
	gender: tournamentGenderSchema.nullable().optional(),
	id: z.string(),
	level: tournamentLevelSchema.nullable().optional(),
	location: z.string().nullable().optional(),
	max_teams: z.number().nullable().optional(),
	name: z.string(),
	registered_teams: z.number().nullable().optional(),
	slug: z.string(),
	start_date: z.string(),
	status: tournamentStatusSchema,
});

export const tournamentListResponseSchema = z.object({
	items: z.array(tournamentListItemSchema),
	limit: z.number(),
	offset: z.number(),
	total: z.number(),
});

const optionalText = z.string().nullable().optional();

/** Go marshals an empty slice it never filled as `null`. */
const listOf = <T extends z.ZodType>(item: T) =>
	z
		.array(item)
		.nullish()
		.transform((items) => items ?? []);

export const tournamentResultSchema = z.object({
	club: optionalText,
	club1: optionalText,
	club2: optionalText,
	place: z.number(),
	player1_name: optionalText,
	player2_name: optionalText,
	points: z.string(),
	prize_money: optionalText,
	team: z.string(),
});

const tournamentAthleteRefSchema = z.object({
	image_url: optionalText,
	slug: z.string(),
});

export const tournamentTeamSchema = z.object({
	club1: optionalText,
	club2: optionalText,
	id: z.string(),
	player1_athlete: tournamentAthleteRefSchema.nullable().optional(),
	player1_name: optionalText,
	player2_athlete: tournamentAthleteRefSchema.nullable().optional(),
	player2_name: optionalText,
	ranking_points: optionalText,
	seed: z.number().nullable().optional(),
	status: optionalText,
	team_name: z.string(),
});

export const tournamentSetScoreSchema = z.object({
	set: z.number(),
	team1: z.number(),
	team2: z.number(),
});

export const tournamentMatchSchema = z.object({
	court: optionalText,
	id: z.string(),
	match_number: z.number().nullable().optional(),
	round_level: z.number().nullable().optional(),
	round_name: optionalText,
	scheduled_at: optionalText,
	score: optionalText,
	set_scores: listOf(tournamentSetScoreSchema),
	status: tournamentMatchStatusSchema,
	team1_name: optionalText,
	team2_name: optionalText,
});

export const tournamentLinkedAthleteSchema = z.object({
	first_name: z.string(),
	image_url: optionalText,
	last_name: z.string(),
	partner: optionalText,
	place: z.number().nullable().optional(),
	points: optionalText,
	slug: z.string(),
});

export const tournamentDetailSchema = tournamentListItemSchema.extend({
	age_category: optionalText,
	deposit: optionalText,
	description: optionalText,
	detail_scraped_at: optionalText,
	entry_fee: optionalText,
	last_scraped_at: z.string(),
	linked_athletes: listOf(tournamentLinkedAthleteSchema),
	matches: listOf(tournamentMatchSchema),
	num_courts: z.number().nullable().optional(),
	organizer: optionalText,
	prize_money: optionalText,
	registration_deadline: optionalText,
	results: listOf(tournamentResultSchema),
	source: z.string(),
	source_id: z.string(),
	source_tourney_id: optionalText,
	source_url: optionalText,
	start_time: optionalText,
	teams: listOf(tournamentTeamSchema),
	tournament_mode: optionalText,
	venue: optionalText,
});
